import React from "react";
import { Box, Typography } from "@mui/material";
import loadingClockGif from "../../Assets/Images/loading_clock.gif";
import analyzingGif from "../../Assets/Images/analyzing.gif";

const VARIANT_GIF = {
  loading: loadingClockGif,
  reminder: analyzingGif,
};

/**
 * Fixed-position toast that shows a loading state (hourglass gif + text).
 * Text is passed from outside. Does not block the page.
 *
 * @param {boolean} open - Whether the toast is visible
 * @param {string} title - Main line (e.g. "Preparing your download")
 * @param {React.ReactNode} [message] - Secondary line (string or JSX, e.g. with a link)
 * @param {'loading'|'reminder'} [variant='loading'] - Which gif to show (`reminder` uses a different asset than downloads)
 * @param {string} [imageSrc] - Optional override for the gif URL (wins over variant)
 * @param {object} [sx] - Optional sx for the root fixed container (e.g. position)
 */
const LoadingToast = ({ open, title, message, sx, variant = "loading", imageSrc }) => {
  if (!open) return null;

  const gifSrc = imageSrc || VARIANT_GIF[variant] || VARIANT_GIF.loading;

  return (
    <Box
      sx={{
        position: "fixed",
        bottom: 24,
        right: 24,
        zIndex: 1300,
        ...sx,
      }}
    >
      <Box
        sx={{
          borderRadius: "12px",
          p: 2,
          minWidth: 260,
          maxWidth: 320,
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          boxShadow: "0 4px 18px rgba(15, 23, 42, 0.15)",
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Box
          component="img"
          src={gifSrc}
          alt=""
          sx={{ width: 40, height: 40, flexShrink: 0, objectFit: "contain" }}
        />
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 600, color: "text.primary", mb: message ? 0.25 : 0 }}>
            {title}
          </Typography>
          {message != null && message !== "" && (
            <Typography variant="body2" color="text.secondary" component="div" sx={{ lineHeight: 1.5 }}>
              {message}
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default LoadingToast;
